import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import "../supplier-info.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AgGridAtom from "../../../../components/atoms/table/ag-grid";
import LV_STATUS from "../../../../constants/lv-status";
import { selectSupplierInfo } from "../../../../store/slices/supplierInfoSlice";
import { formatCurrencyToLocale, getFormattedDate } from "../../../../utils/method";

const SupplementsSection = () => {
	const { t } = useTranslation();
	const [isCollapsed, setIsCollapsed] = useState(true);

	const supplierInfo = useSelector(selectSupplierInfo);
	const supplements = supplierInfo?.supplements || {};

	const toggleCollapse = () => setIsCollapsed(!isCollapsed);

	// Define column definitions for AG Grid
	const columnDefs = useMemo(
		() => [
			{
				headerName: t("supplier-info.supplements.nr"),
				field: "nr",
				valueGetter: (params) => `${params.node.rowIndex + 1}.`,
				width: 70,
			},
			{
				headerName: t("supplier-info.supplements.amendment-code"),
				field: "orderNumber",
			},
			{
				headerName: t("supplier-info.supplements.description"),
				field: "name",
				// Add a valueGetter to include the key in the description
				valueGetter: (params) => {
					const { key, name } = params.data || {};
					return [key, name].filter(Boolean).join(" - ") || "";
				},
			},
			{
				headerName: t("supplier-info.supplements.status-date"),
				field: "capmoUpdatedAt",
				type: "numericColumn",
				valueFormatter: (params) => getFormattedDate(params.value),
			},
			{
				headerName: t("supplier-info.supplements.pending-amount"),
				field: "pendingAmount",
				cellStyle: {
					backgroundColor: "rgba(255, 193, 7, 0.1)",
				},
				headerClass: "bg-warning-subtle text-end",
				type: "numericColumn",
				valueGetter: (params) => {
					const { statusCode, cost } = params.data || {};
					if ([LV_STATUS.APPROVED, LV_STATUS.REJECTED].includes(statusCode)) {
						return "";
					}
					return cost ? `${formatCurrencyToLocale(cost)}` : "";
				}
			},
			{
				headerName: t("supplier-info.supplements.confirmed-amount"),
				field: "confirmedAmount",
				cellStyle: {
					backgroundColor: "rgba(25, 135, 84, 0.1)",
				},
				type: "numericColumn",
				valueGetter: (params) => {
					const { statusCode, cost } = params.data || {};
					if ([LV_STATUS.APPROVED].includes(statusCode)) {
						return cost ? `${formatCurrencyToLocale(cost)}` : "";
					}
				},
				headerClass: "bg-success-subtle text-end",
			},
		],
		[t],
	);

	return (
		<Card className="mb-3">
			<Card.Header
				onClick={toggleCollapse}
				className="bg-light si-clickable-header"
			>
				<div className="d-flex justify-content-between align-items-center">
					<span className="fw-bold">
						{t("supplier-info.supplements.title")}
					</span>
					<div className="d-flex align-items-center">
						<div className="d-flex me-3">
							<div className="flex-grow-1 text-center bg-warning-subtle px-2">
								<b>
									{t(
										"supplier-info.supplements.pending-supplements",
									)}
									:{" "}
									{formatCurrencyToLocale(supplements?.pending ?? 0)}
								</b>
							</div>
							<div className="flex-grow-1 text-center bg-success-subtle px-2">
								<b>
									{t(
										"supplier-info.supplements.confirmed-supplements",
									)}
									:{" "}
									{formatCurrencyToLocale(
										supplements?.confirmed ?? 0,
									)}
								</b>
							</div>
						</div>
						<FontAwesomeIcon
							icon={isCollapsed ? faChevronDown : faChevronUp}
						/>
					</div>
				</div>
			</Card.Header>

			{!isCollapsed && (
				<Card.Body className="si-card-body-grid">
					<div className="si-grid-275">
						<AgGridAtom
							columnDefs={columnDefs}
							rowData={supplements?.items || []}
							defaultColDef={{
								flex: 1,
								sortable: false,
								wrapText: true,
								autoHeight: false,
							}}
						/>
					</div>
				</Card.Body>
			)}
		</Card>
	);
};

export default SupplementsSection;
